import { Component, OnChanges, SimpleChanges, Input, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AccountTouchPointSummary, SessionSummary, CustomerTouchPoint } from '@eucoms/customer-touchpoints-tracker';
import { ApiService } from '../../services/api.service';
import { catchError, map, takeUntil, filter } from 'rxjs/operators';
import { of, Subject, BehaviorSubject } from 'rxjs';

// Interface for API response
interface TouchpointsResponse {
  items?: CustomerTouchPoint[];
}

@Component({
  selector: 'app-summary-details',
  templateUrl: './summary-details.component.html',
  styleUrls: ['./summary-details.component.scss']
})
export class SummaryDetailsComponent implements OnChanges, OnDestroy {

  @Input() summary: AccountTouchPointSummary | null = null;
  @Input() timezone: string = 'America/New_York';

  private readonly MAX_VISIBLE_TOUCHPOINTS = 4;
  private readonly MAX_EXPANDED_TOUCHPOINTS = 10;

  // Cache ALL touchpoints for this account (loaded once)
  private allAccountTouchpoints: CustomerTouchPoint[] = [];
  private touchpointsLoaded$ = new BehaviorSubject<boolean>(false);
  private touchpointsLoading: boolean = false;

  // Touchpoint data management per session
  sessionTouchpoints: { [sessionId: string]: CustomerTouchPoint[] } = {};
  sessionLoading: { [sessionId: string]: boolean } = {};

  // Track which sessions are expanded (showing touchpoints)
  expandedSessions: { [sessionId: string]: boolean } = {};

  showAllTouchpoints: { [sessionId: string]: boolean } = {};

  // Subject for cleanup
  private destroy$ = new Subject<void>();

  // Getter for accountNumber from summary
  get accountNumber(): string {
    return this.summary?.accountNumber || '';
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private api: ApiService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['summary'] && this.summary) {
      // Clear all dictionaries to prevent memory leaks when summary changes
      this.showAllTouchpoints = {};
      this.expandedSessions = {};
      this.sessionTouchpoints = {};
      this.sessionLoading = {};

      // Reset touchpoint cache for new summary
      this.allAccountTouchpoints = [];
      this.touchpointsLoaded$.next(false);
      this.touchpointsLoading = false;

      // Load touchpoints for this account upfront
      this.loadAllAccountTouchpoints();

      // By default, expand only the most recent session
      if (this.summary.sessionSummaries && this.summary.sessionSummaries.length > 0) {
        const mostRecentSession = this.summary.sessionSummaries[0];
        this.expandedSessions[mostRecentSession.sessionId] = true;
        
        // Ensure loading state shows while initial cache is fetching
        if (!this.touchpointsLoaded$.value) {
          this.sessionLoading[mostRecentSession.sessionId] = true;

          this.touchpointsLoaded$.asObservable().pipe(
            filter(loaded => loaded === true),
            takeUntil(this.destroy$)
          ).subscribe(() => {
            this.sessionLoading[mostRecentSession.sessionId] = false;
            this.populateSessionFromCache(mostRecentSession.sessionId);
          });
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.touchpointsLoaded$.complete();
  }

  // Load all touchpoints for the current account and cache them
  private loadAllAccountTouchpoints(): void {
    if (this.touchpointsLoading || this.touchpointsLoaded$.value) {
      return;
    }

    if (!this.accountNumber) {
      console.warn('Cannot load touchpoints: No account number');
      return;
    }

    this.touchpointsLoading = true;

    const filterParams = {
      accountNumber: this.accountNumber
    };

    this.api.getTouchpointsByFilter(filterParams).pipe(
      takeUntil(this.destroy$),
      map((response: TouchpointsResponse | CustomerTouchPoint[]) => {
        const touchpoints: CustomerTouchPoint[] = Array.isArray(response) ? response : (response.items || []);

        // Sort by timestamp descending (newest first)
        return touchpoints.sort((a, b) => {
          const timeA = new Date(a.timestamp || 0).getTime();
          const timeB = new Date(b.timestamp || 0).getTime();
          return timeB - timeA;
        });
      }),
      catchError(err => {
        console.error('Failed to load touchpoints for account:', err);
        this.touchpointsLoading = false;

        // Notify that loading is complete
        this.touchpointsLoaded$.next(true);

        return of([]);
      })
    ).subscribe((touchpoints: CustomerTouchPoint[]) => {
      // Cache all touchpoints
      this.allAccountTouchpoints = touchpoints;
      this.touchpointsLoading = false;

      this.touchpointsLoaded$.next(true);

      // Populate the most recent session that was expanded by default
      if (this.summary?.sessionSummaries && this.summary.sessionSummaries.length > 0) {
        const mostRecentSession = this.summary.sessionSummaries[0];
        if (this.expandedSessions[mostRecentSession.sessionId]) {
          this.populateSessionFromCache(mostRecentSession.sessionId);
        }
      }
    });
  }

  // Populate a session's touchpoints from the cached data
  private populateSessionFromCache(sessionId: string): void {
    if (!this.summary?.sessionSummaries) {
      return;
    }

    const session = this.summary.sessionSummaries.find(s => s.sessionId === sessionId);
    if (!session || !session.touchPointIds || session.touchPointIds.length === 0) {
      this.sessionTouchpoints[sessionId] = [];
      return;
    }

    const sessionTouchpointIds = new Set(session.touchPointIds);

    // Filter cached touchpoints to only those in this session's touchPointIds array
    const sessionTouchpoints = this.allAccountTouchpoints.filter(tp =>
      sessionTouchpointIds.has(tp.id)
    );

    // Apply limit based on view more state
    const limit = this.showAllTouchpoints[sessionId]
      ? this.MAX_EXPANDED_TOUCHPOINTS
      : this.MAX_VISIBLE_TOUCHPOINTS;

    this.sessionTouchpoints[sessionId] = sessionTouchpoints.slice(0, limit);
  }

  toggleSessionExpansion(sessionId: string): void {
    // Toggle the expanded state
    this.expandedSessions[sessionId] = !this.expandedSessions[sessionId];

    // If expanding and touchpoints haven't been populated yet
    if (this.expandedSessions[sessionId] && !this.sessionTouchpoints[sessionId]) {
      if (this.touchpointsLoaded$.value) {
        // Touchpoints already cached - populate immediately
        this.populateSessionFromCache(sessionId);
      } else {
        // Still loading - set loading state and subscribe to when data arrives
        this.sessionLoading[sessionId] = true;

        this.touchpointsLoaded$.asObservable().pipe(
          filter(loaded => loaded === true),
          takeUntil(this.destroy$)
        ).subscribe(() => {
          this.sessionLoading[sessionId] = false;
          this.populateSessionFromCache(sessionId);
        });
      }
    }
  }

  isSessionExpanded(sessionId: string): boolean {
    return !!this.expandedSessions[sessionId];
  }

  getTouchpointById(sessionId: string, touchpointId: string): CustomerTouchPoint | null {
    const touchpoints = this.sessionTouchpoints[sessionId] || [];
    return touchpoints.find(tp => tp.id === touchpointId) || null;
  }

  getTouchpointTimestamp(sessionId: string, touchpointId: string): Date | null {
    const touchpoint = this.getTouchpointById(sessionId, touchpointId);
    return touchpoint?.timestamp || null;
  }

  getIndividualTouchpointType(sessionId: string, touchpointId: string): string {
    const touchpoint = this.getTouchpointById(sessionId, touchpointId);
    return touchpoint?.type || touchpoint?.channel || 'Unknown';
  }

  getIndividualResultCode(sessionId: string, touchpointId: string): string {
    const touchpoint = this.getTouchpointById(sessionId, touchpointId);
    return touchpoint?.processingContext?.resultCode || '-';
  }

  getIndividualTouchpointStatus(sessionId: string, touchpointId: string): string {
    const touchpoint = this.getTouchpointById(sessionId, touchpointId);
    return touchpoint?.status || 'Unknown';
  }

  getChannelIcon(channel: string): string {
    // Function to get the icon for the channel type
    if (channel) {
      switch (channel) {
        case 'Web':
          return 'feather/monitor';
        case 'MobileApp':
          return 'feather/smartphone';
        case 'Chatbot':
          return 'feather/bot';
        case 'IVR':
          return 'feather/phone-call';
        case 'CSR':
          return 'feather/headphones';
        case 'Proactive':
          return 'feather/code-2';
        case 'Unknown':
          return 'feather/help-circle';
        default:
          return 'feather/help-circle';
      }
    } else {
      return 'feather/help-circle';
    }
  }

  getSessionSummaryText(summary?: SessionSummary): string {
    if (summary) {
      if (summary.summaryText?.length > 0) {
        return summary.summaryText;
      }
    }
    return 'No summary to show';
  }

  getUpvoteCount(session: SessionSummary): number {
    return session.feedbackResponses?.filter(r => r.status === 'UpVote').length ?? 0;
  }

  getDownvoteCount(session: SessionSummary): number {
    return session.feedbackResponses?.filter(r => r.status === 'DownVote').length ?? 0;
  }

  getUpvoteTooltip(session: SessionSummary): string {
    if (!session.feedbackResponses) return 'No upvotes yet';
    const voters = session.feedbackResponses
      .filter(r => r.status === 'UpVote')
      .map(r => r.csrId);
    return voters.length ? `Upvoted by: ${voters.join(', ')}` : 'No upvotes yet';
  }

  getDownvoteTooltip(session: SessionSummary): string {
    if (!session.feedbackResponses) return 'No downvotes yet';
    const voters = session.feedbackResponses
      .filter(r => r.status === 'DownVote')
      .map(r => r.csrId);
    return voters.length ? `Downvoted by: ${voters.join(', ')}` : 'No downvotes yet';
  }

  navigateToTouchpoint(touchpointId: string): void {
    if (!touchpointId) {
      console.warn('No touchpoint ID provided');
      return;
    }

    // Get the OpCo from the summary, or fallback to current route parameter
    const opCo = this.summary?.opCo?.toLowerCase() || this.route.snapshot.paramMap.get('opCo')?.toLowerCase();

    if (!opCo) {
      console.error('Unable to determine OpCo for navigation');
      return;
    }

    // Navigate to touchpoints tab with OpCo in route and query parameters
    this.router.navigate([opCo, 'touchpoints'], {
      queryParams: {
        accountNumber: this.accountNumber,
        touchpointId: touchpointId
      }
    });
  }

  hasMoreThanFourTouchpoints(sessionId: string): boolean {
    const session = this.summary?.sessionSummaries?.find(s => s.sessionId === sessionId);
    return (session?.touchPointIds?.length || 0) > this.MAX_VISIBLE_TOUCHPOINTS;
  }

  toggleViewMore(sessionId: string): void {
    // Only toggle if touchpoints are loaded
    if (!this.touchpointsLoaded$.value) {
      console.warn('Cannot toggle view more: touchpoints not yet loaded');
      return;
    }

    // Toggle view more state for THIS specific session
    this.showAllTouchpoints[sessionId] = !this.showAllTouchpoints[sessionId];

    // Re-populate from cache with new limit
    this.populateSessionFromCache(sessionId);
  }

  getSessionTouchpointIds(session: SessionSummary): string[] {
    // Get sorted touchpoints from our fetched data
    const sortedTouchpoints = this.sessionTouchpoints[session.sessionId] || [];
    const limit = this.showAllTouchpoints[session.sessionId] ? this.MAX_EXPANDED_TOUCHPOINTS : this.MAX_VISIBLE_TOUCHPOINTS;

    // Return IDs from sorted touchpoints (already sorted by timestamp descending)
    return sortedTouchpoints.slice(0, limit).map((tp: CustomerTouchPoint) => tp.id);
  }
}
